import React, {useContext, useEffect} from "react";
import {
	Box,
	Button,
	FormHelperText,
	FormLabel,
	Grid,
	Link,
	Stack,
	Typography,
	useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useForm, FormProvider} from "react-hook-form";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {bookingDataValueState, customerDataValueState, raFillingAccordionValidationState, stepState} from "../state";
import {IConsentMgmtForm} from "../../models/models";
import {FormCheckbox} from "../../common/form-fields-wrappers";
import {CONSENT_MGMT_INITIAL_VALUE, CONSENT_MGMT_VALIDATION_SCHEMA} from "./config";
import {consentManagement} from "../services/http.services";
import CustomLoader from "../../../../util/component/CustomLoader";
import {yupResolver} from "@hookform/resolvers/yup";
import FileUploader from "../../../../util/component/FileUploader";
import {useMutation} from "@tanstack/react-query";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

const ConsentMgmt = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const {dispatchGlobalData} = useContext(GlobalContext);
	const [step, setStep] = useRecoilState(stepState);
	const customerDataValue = useRecoilValue(customerDataValueState);
	const bookingEntity = useRecoilValue(bookingDataValueState);

	const consentMgmtForm = useForm<IConsentMgmtForm>({
		defaultValues: CONSENT_MGMT_INITIAL_VALUE,
		resolver: yupResolver(CONSENT_MGMT_VALIDATION_SCHEMA)
	});

	const errors: any = consentMgmtForm.formState.errors;

	const setRaFillingAccordionValidation = useSetRecoilState(raFillingAccordionValidationState);
	
	const viewDefaultError = (mess: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(mess),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	const consentManagementMutation = useMutation(
		consentManagement,
		{
			onSuccess: (response: any) => {
				if (!!response.data) {
					const completed = step.completed.concat();
					completed[step.active] = true;
					setStep({
						active: step.active + 1,
						completed,
						numSteps: step.numSteps
					});
				} else {
					consentMgmtForm.setValue('consents.extendedPromotionsDisagree', response.extendedPromotionsDisagree);
					consentMgmtForm.setValue('consents.extendedPromotionsAgree', response.extendedPromotionsAgree);
					consentMgmtForm.setValue('consents.relatedPromotionsDisagree', response.relatedPromotionsDisagree);
					consentMgmtForm.setValue('consents.relatedPromotionsAgree', response.relatedPromotionsAgree);
					consentMgmtForm.setValue('consents.commercialPromotionsDisagree', response.commercialPromotionsDisagree);
					consentMgmtForm.setValue('consents.commercialPromotionsAgree', response.commercialPromotionsAgree);
					consentMgmtForm.setValue('acknowledgment', response.acknowledgment);
				}
			},
			onError: (error: any) => {
				viewDefaultError('translation:error.tryLater');
			}
		});

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (step.active > 0) {

			setStep({
				active: step.active - 1,
				completed: step.completed,
				numSteps: step.numSteps
			});
			
			setRaFillingAccordionValidation({
				generalInfo: true,
				firstBillingOwnerInfo: false,
				secondBillingOwnerInfo: false,
				ancillariesInfo: false
			});
		}
	}

	const handleNextStep = (data: IConsentMgmtForm) => {
		// upload bin file
		const form = new FormData();
		// @ts-ignore
		form.append('file', data.consents.file);

		const consentManagements: any = {
			idRentalAgreement: 			bookingEntity.idRentalAgreement,
			fiscalCode: 				customerDataValue.mainDriver.fiscalCode,
			acknowledgment: 			data.acknowledgment,
			extendedPromotionsAgree: 	data.consents.extendedPromotionsAgree,
			extendedPromotionsDisagree: data.consents.extendedPromotionsDisagree,
			relatedPromotionsAgree: 	data.consents.relatedPromotionsAgree,
			relatedPromotionsDisagree: 	data.consents.relatedPromotionsDisagree,
			commercialPromotionsAgree: 	data.consents.commercialPromotionsAgree,
			commercialPromotionsDisagree: data.consents.commercialPromotionsDisagree,
			customerId: bookingEntity.raOwner.cbId,
			idTenant: bookingEntity.idTenant,
			dealer: bookingEntity.dealer,
		}

		form.append('consentManagements', JSON.stringify(consentManagements));
		consentManagementMutation.mutate(form as any);
	}

	const handleCheckEquipment = (e: React.SyntheticEvent, consents: string, type: "agree" | "disagree", value: boolean) => {
		e.stopPropagation();

		switch (consents) {
			case 'extended':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.extendedPromotionsDisagree', false);
				} else {
					consentMgmtForm.setValue('consents.extendedPromotionsAgree', false);
				}
				break;
			case 'related':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.relatedPromotionsDisagree', false);
				} else {
					consentMgmtForm.setValue('consents.relatedPromotionsAgree', false);
				}
				break;
			case 'commercial':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.commercialPromotionsDisagree', false);
				} else {
					consentMgmtForm.setValue('consents.commercialPromotionsAgree', false);
				}
				break;
		}

		consentMgmtForm.clearErrors()
	}

	const handleUpload = async (file: File) => {
		consentMgmtForm.setValue('consents.file', file);
	}

	const handleRemove = () => {
		consentMgmtForm.setValue('consents.file', null);
	}

	useEffect(() => {
		const form = new FormData();
		form.append('consentManagements', JSON.stringify({
			fiscalCode: customerDataValue?.mainDriver?.fiscalCode,
			customerId: bookingEntity.raOwner.cbId,
			read: true,
			idTenant: bookingEntity.idTenant,
			dealer: bookingEntity.dealer,
		}));
		consentManagementMutation.mutate(form as any);
	}, [])

	return (
		<>
			<FormProvider {...consentMgmtForm}>
				<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
					<Grid item xs={12}>
						<Typography variant={"h6"}>
							{t("consentMgmt.privacySection.title")}
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<Typography variant={"body"}>
							{t("consentMgmt.privacySection.availableOn")}
						</Typography>
						<Link href="https://www.drivalia.it/it/customer-service/privacyservizio/" color={"secondary"} variant={"h6"} target={"_blank"}>
							https://www.drivalia.it/it/customer-service/privacyservizio/
						</Link>
					</Grid>

					<Grid container item xs={12} sx={{mt: theme.spacing(2)}}>
						<Grid item xs={12}>
							<Typography variant={"body"}>
								{t("consentMgmt.privacySection.clientInfo")}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<FormCheckbox<IConsentMgmtForm>
								name={"acknowledgment"}
								label={t("consentMgmt.privacySection.acknowledgment")}
								color={'secondary'}
								sx={{color: (errors?.acknowledgment !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							/>
						</Grid>
					</Grid>

					{/* Consents section */}
					<Grid item xs={12} sx={{mt: theme.spacing(2)}}>
						<Typography variant={"h6"}>
							{t("consentMgmt.consentsSection.title")}
						</Typography>
					</Grid>
					<Grid item xs={12} >
						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.selfDeclaration")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{mt: theme.spacing(2)}}>
						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.consentsInfo")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{mt: theme.spacing(2), pl: theme.spacing(2)}}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.extendedPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.extendedPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'extended', 'agree', value)}} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.extendedPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.extendedPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'extended', 'disagree', value)}} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.extendedPromotionsText")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{mt: theme.spacing(2), pl: theme.spacing(2)}}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.relatedPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.relatedPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'related', 'agree', value)}} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.relatedPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.relatedPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'related', 'disagree', value)}} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.relatedPromotionsText")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{mt: theme.spacing(2), pl: theme.spacing(2)}}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.commercialPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.commercialPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'commercial', 'agree', value)}} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.commercialPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{color: (errors?.consents?.commercialPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main}}
							onChange={(e: React.SyntheticEvent, value: boolean) => {handleCheckEquipment(e, 'commercial', 'disagree', value)}} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.commercialPromotionsText")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', mt: theme.spacing(2)}}>
						<Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
							<Stack>
								<FormLabel required={!!errors.consents?.file}
										   error={!!errors.consents?.file}>
									{t("consentMgmt.uploadPaperConsents")}
								</FormLabel>
							</Stack>
							<FileUploader
								onUpload={handleUpload}
								onRemove={handleRemove}
								acceptedFiles={{'image/png': ['.png', '.jpeg', '.jpg']}}
							/>

							{
								!!errors.consents?.signedMgmt &&
								<FormHelperText error>
									{t('common.error.required')}
								</FormHelperText>
							}
						</Box>

					</Grid>
				</Grid>

				<Box sx={{display: 'flex',  justifyContent: 'space-between', mt: theme.spacing(2)}}>
					{step.active === 0 ? <Box></Box> : <Button variant={'text'} color={"primary"}  onClick={handleBackStep}>{t('common.button.backStep')}</Button>}
					{step.active ===  (step.numSteps -1) ? <Box></Box> : <Button variant={'outlined'} color={"accent"} onClick={consentMgmtForm.handleSubmit(handleNextStep)}>{t('common.button.nextStep')}</Button>}
				</Box>
			</FormProvider>

			<CustomLoader loading={consentManagementMutation.isLoading}/>
		</>
	)
}

export default ConsentMgmt;
